import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import {
  CONFIG_DRAWER_WIDTH_STORAGE_KEY,
  usePersistedDrawerWidth,
} from './usePersistedDrawerWidth';

export interface ConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  storageKey?: string;
  'aria-label'?: string;
}

export default function ConfigDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  storageKey = CONFIG_DRAWER_WIDTH_STORAGE_KEY,
  'aria-label': ariaLabel = 'Configuration panel',
}: ConfigDrawerProps) {
  const { width, isResizing, handleResizePointerDown } =
    usePersistedDrawerWidth({ storageKey });

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      hideBackdrop
      aria-label={ariaLabel}
      ModalProps={{ keepMounted: true }}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 2,
        pointerEvents: open ? 'auto' : 'none',
        '& .MuiDrawer-paper': {
          width,
          height: '100dvh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderLeft: 1,
          borderColor: 'divider',
          transition: isResizing ? 'none' : undefined,
        },
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize configuration panel"
        onPointerDown={handleResizePointerDown}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          userSelect: 'none',
          color: 'text.disabled',
          '&:hover': {
            color: 'text.secondary',
            bgcolor: 'action.hover',
          },
        }}
      >
        <DragIndicatorIcon
          fontSize="small"
          sx={{ transform: 'rotate(90deg)', pointerEvents: 'none' }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          pl: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" noWrap>
            {title}
          </Typography>
          {subtitle ? (
            <Box sx={{ mt: 0.25 }}>{subtitle}</Box>
          ) : null}
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pl: 1,
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}
